import { useEffect, useState, type ReactNode, type ChangeEvent } from "react";
import {
  Box,
  Typography,
  Button,
  Modal,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  MenuItem,
  Autocomplete,
  Checkbox,
} from "@mui/material";
// icons plus utilisés après passage en VideoCard
import VideoCard from "../components/VideoCard";
import { getData, insertData, updateData, deleteData, uploadVideoFile, resolveMediaUrl } from "../api";
import { useNavigate } from "react-router-dom";
import { filterOutSpecialCategories } from "../utils/specialCategories";

interface Video {
  id?: number;
  title: string;
  description: string;
  category_id: number | "";
  url: string;
}

interface Category {
  id: number;
  name: string;
}

export default function ManageVideos() {
  const [videos, setVideos] = useState<Video[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [videoUsages, setVideoUsages] = useState<Record<string, { id: number; title: string }[]>>({});
  const [exercises, setExercises] = useState<Array<{ id: number; title: string; recommended_videos?: string | any[] }>>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentVideo, setCurrentVideo] = useState<Video>({ title: "", description: "", category_id: "", url: "" });
  const [formErrors, setFormErrors] = useState({ title: false, url: false, category_id: false });
  const [isFileUpload, setIsFileUpload] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewVideo, setPreviewVideo] = useState<Video | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignVideo, setAssignVideo] = useState<Video | null>(null);
  const [selectedExIds, setSelectedExIds] = useState<number[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    refreshVideos();
    getData("exercise_categories").then((res: any) => {
      const dedupe = (arr: Category[]) => {
        const seen = new Set<string>();
        const out: Category[] = [];
        (arr || []).forEach((c) => {
          const key = (c.name || "").trim().toLowerCase();
          if (!seen.has(key)) {
            seen.add(key);
            out.push(c);
          }
        });
        return out;
      };

      setCategories(filterOutSpecialCategories(dedupe(res.data || [])));
    });
    // Charger les exercices pour recenser l'utilisation des vidéos
    getData("exercises").then((res: any) => {
      const list = (res.data || []) as Array<{ id: number; title: string; recommended_videos?: string | any[] }>;
      setExercises(list);
      const usage: Record<string, { id: number; title: string }[]> = {};
      list.forEach((exo) => {
        try {
          let vids: any = exo.recommended_videos;
          if (typeof vids === "string") vids = JSON.parse(vids);
          if (Array.isArray(vids)) {
            vids.map((v) => v.toString()).forEach((vid: string) => {
              if (!usage[vid]) usage[vid] = [];
              usage[vid].push({ id: exo.id, title: exo.title });
            });
          }
        } catch {
          // ignore parse errors
        }
      });
      setVideoUsages(usage);
    });
  }, []);

  const refreshVideos = () => {
    getData("videos", { order_by: "id DESC" }).then((res: any) => {
      setVideos(res.data || []);
    });
  };

  const handleOpenAssign = (video: Video) => {
    setAssignVideo(video);
    const cur = videoUsages[video.id?.toString() || ""] || [];
    setSelectedExIds(cur.map((u) => u.id));
    setAssignOpen(true);
  };

  const handleSaveAssign = async () => {
    if (!assignVideo?.id) {
      setAssignOpen(false);
      return;
    }
    const vidStr = assignVideo.id.toString();
    const selectedSet = new Set(selectedExIds);
    const updates: Promise<any>[] = [];

    exercises.forEach((exo) => {
      let vids: any = exo.recommended_videos;
      try {
        if (typeof vids === "string") vids = JSON.parse(vids);
      } catch {
        vids = [];
      }
      if (!Array.isArray(vids)) vids = [];
      vids = vids.map((v: any) => v.toString());

      const has = vids.includes(vidStr);
      const should = selectedSet.has(exo.id);
      if (has !== should) {
        let newVids: string[];
        if (should) {
          newVids = Array.from(new Set([...vids, vidStr]));
        } else {
          newVids = vids.filter((v: string) => v !== vidStr);
        }
        updates.push(updateData("exercises", { recommended_videos: newVids }, `id = ${exo.id}`));
      }
    });

    try {
      await Promise.all(updates);
      const res = await getData("exercises");
      const list = (res.data || []) as Array<{ id: number; title: string; recommended_videos?: string | any[] }>;
      setExercises(list);
      const usage: Record<string, { id: number; title: string }[]> = {};
      list.forEach((exo) => {
        try {
          let vids: any = exo.recommended_videos;
          if (typeof vids === "string") vids = JSON.parse(vids);
          if (Array.isArray(vids)) {
            vids.map((v) => v.toString()).forEach((vid: string) => {
              if (!usage[vid]) usage[vid] = [];
              usage[vid].push({ id: exo.id, title: exo.title });
            });
          }
        } catch {}
      });
      setVideoUsages(usage);
    } catch (e: any) {
      setErrorMessage(e?.message || "Échec de l'association aux exercices.");
    } finally {
      setAssignOpen(false);
      setAssignVideo(null);
    }
  };

  const getYouTubeId = (url: string): string | null => {
    const match = url.match(/(?:[?&]v=|\.be\/|\/embed\/|\/shorts\/)([\w-]+)/);
    return match ? match[1] : null;
  };

  const validateForm = () => {
    const errors = {
      title: !currentVideo.title.trim(),
      url: !isFileUpload && !currentVideo.url.trim(),
      category_id: !currentVideo.category_id,
    };
    setFormErrors(errors);
    if (isFileUpload && !selectedFile) {
      setErrorMessage("Veuillez sélectionner un fichier vidéo.");
      return false;
    }
    if (!isFileUpload && !currentVideo.url.trim()) {
      setErrorMessage("Veuillez renseigner le titre, la catégorie et l'URL, ou activer l'upload de fichier.");
    }
    return !Object.values(errors).some(Boolean);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const handleOpenModal = (video?: Video) => {
    if (video) {
      setCurrentVideo({ id: video.id, title: video.title, description: video.description, category_id: video.category_id, url: video.url });
    } else {
      setCurrentVideo({ title: "", description: "", category_id: "", url: "" });
    }
    setIsFileUpload(false);
    setSelectedFile(null);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setIsFileUpload(false);
    setSelectedFile(null);
    setFormErrors({ title: false, url: false, category_id: false });
  };

  const handleOpenDeleteModal = (id: number) => {
    setVideoToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setVideoToDelete(null);
    setDeleteModalOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (videoToDelete == null) return;
    try {
      await deleteData("videos", `id = ${videoToDelete}`);
      setVideos((prev) => prev.filter((v) => v.id !== videoToDelete));
    } catch (e: any) {
      setErrorMessage(e?.message || "Impossible de supprimer la vidéo.");
    } finally {
      handleCloseDeleteModal();
    }
  };

  const handleSaveVideo = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      if (isFileUpload && !currentVideo.id) {
        if (!selectedFile) {
          setErrorMessage("Veuillez sélectionner un fichier vidéo.");
          return;
        }

        const uploadResponse = await uploadVideoFile(selectedFile);
        const videoUrl = uploadResponse.url || uploadResponse.video_url || uploadResponse.path;

        if (!videoUrl) {
          throw new Error("L'API n'a pas renvoyé l'URL de la vidéo.");
        }

        await insertData("videos", {
          title: currentVideo.title,
          description: currentVideo.description,
          category_id: currentVideo.category_id,
          url: videoUrl,
        });
      } else if (currentVideo.id) {
        await updateData(
          "videos",
          {
            title: currentVideo.title,
            description: currentVideo.description,
            category_id: currentVideo.category_id,
            url: currentVideo.url,
          },
          `id = ${currentVideo.id}`
        );
      } else {
        await insertData("videos", {
          title: currentVideo.title,
          description: currentVideo.description,
          category_id: currentVideo.category_id,
          url: currentVideo.url,
        });
      }
      refreshVideos();
      handleCloseModal();
    } catch (e: any) {
      setErrorMessage(e?.message || "Échec de l'enregistrement de la vidéo.");
    }
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Button
        variant="outlined"
        color="primary"
        onClick={() => navigate("/dashboard")}
        sx={{ marginBottom: 4 }}
      >
        ← Retour au Tableau de bord
      </Button>
      <Typography variant="h4" sx={{ marginBottom: 2 }}>
        Gestion des vidéos
      </Typography>
      <Button variant="contained" onClick={() => handleOpenModal()} sx={{ marginBottom: 4 }}>
        Ajouter une vidéo
      </Button>

      {/* Groupement par catégorie */}
      {(() => {
        const byCat: Record<string, Video[]> = {};
        videos.forEach((v) => {
          const key = (v.category_id ?? "").toString() || "uncategorized";
          if (!byCat[key]) byCat[key] = [];
          byCat[key].push(v);
        });

        const orderedCats = [...categories].sort((a, b) => a.name.localeCompare(b.name));
        const sections: ReactNode[] = [];

        orderedCats.forEach((cat) => {
          const list = byCat[cat.id.toString()] || [];
          if (list.length === 0) return;
          sections.push(
            <Box key={cat.id} sx={{ mb: 3 }}>
              <Typography variant="h5" sx={{ mb: 1 }}>{cat.name}</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr', lg: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
                {list.map((video) => (
                  <VideoCard
                    key={video.id}
                    title={video.title}
                    description={video.description}
                    categoryName={cat.name}
                    url={video.url}
                    usedInTitles={(videoUsages[video.id?.toString() || ""] || []).map((u) => u.title)}
                    onView={() => { setPreviewVideo(video); setPreviewOpen(true); }}
                    onEdit={() => handleOpenModal(video)}
                    onAssign={() => handleOpenAssign(video)}
                    onDelete={() => handleOpenDeleteModal(video.id!)}
                  />
                ))}
              </Box>
            </Box>
          );
        });

        const uncategorized = byCat["uncategorized"] || [];
        if (uncategorized.length > 0) {
          sections.push(
            <Box key="uncategorized" sx={{ mb: 3 }}>
              <Typography variant="h5" sx={{ mb: 1 }}>Sans catégorie</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr', lg: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
                {uncategorized.map((video) => (
                  <VideoCard
                    key={video.id}
                    title={video.title}
                    description={video.description}
                    categoryName={undefined}
                    url={video.url}
                    usedInTitles={(videoUsages[video.id?.toString() || ""] || []).map((u) => u.title)}
                    onView={() => { setPreviewVideo(video); setPreviewOpen(true); }}
                    onEdit={() => handleOpenModal(video)}
                    onAssign={() => handleOpenAssign(video)}
                    onDelete={() => handleOpenDeleteModal(video.id!)}
                  />
                ))}
              </Box>
            </Box>
          );
        }

        return <>{sections}</>;
      })()}
      
      <Modal open={modalOpen} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 420,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography variant="h6" sx={{ marginBottom: 2 }}>
            {currentVideo.id ? "Modifier la vidéo" : "Ajouter une vidéo"}
          </Typography>
          <TextField
            label="Titre"
            fullWidth
            value={currentVideo.title}
            onChange={(e) => setCurrentVideo({ ...currentVideo, title: e.target.value })}
            error={formErrors.title}
            helperText={formErrors.title ? "Le titre est obligatoire." : ""}
            sx={{ marginBottom: 2 }}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={currentVideo.description}
            onChange={(e) => setCurrentVideo({ ...currentVideo, description: e.target.value })}
            sx={{ marginBottom: 2 }}
          />
          <TextField
            label="Catégorie"
            select
            fullWidth
            value={currentVideo.category_id}
            onChange={(e) => setCurrentVideo({ ...currentVideo, category_id: Number(e.target.value) })}
            error={formErrors.category_id}
            helperText={formErrors.category_id ? "La catégorie est obligatoire." : ""}
            sx={{ marginBottom: 2 }}
          >
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.name}
              </MenuItem>
            ))}
          </TextField>
          {!currentVideo.id && (
            <Box sx={{ mb: 2 }}>
              <Checkbox
                checked={isFileUpload}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setIsFileUpload(checked);
                  if (checked) {
                    setCurrentVideo({ ...currentVideo, url: "" });
                  }
                }}
                sx={{ mr: 1 }}
              />
              <Typography variant="body2" component="span">
                Uploader une vidéo sur le serveur (au lieu d'une URL)
              </Typography>
            </Box>
          )}

          {isFileUpload && !currentVideo.id ? (
            <Box sx={{ mb: 2 }}>
              <Button variant="outlined" component="label">
                Sélectionner un fichier vidéo
                <input type="file" hidden accept="video/*" onChange={handleFileChange} />
              </Button>
              {selectedFile ? (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Fichier sélectionné : {selectedFile.name}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Aucun fichier sélectionné.
                </Typography>
              )}
            </Box>
          ) : (
            <TextField
              label="URL (YouTube, Vimeo, etc.)"
              fullWidth
              value={currentVideo.url}
              onChange={(e) => setCurrentVideo({ ...currentVideo, url: e.target.value })}
              error={!isFileUpload && formErrors.url}
              helperText={!isFileUpload && formErrors.url ? "L'URL est obligatoire." : ""}
              sx={{ marginBottom: 2 }}
            />
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button onClick={handleCloseModal} color="secondary">
              Annuler
            </Button>
            <Button onClick={handleSaveVideo} variant="contained">
              Enregistrer
            </Button>
          </Box>
        </Box>
      </Modal>

      <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer cette vidéo ? Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal} color="secondary">Annuler</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">Supprimer</Button>
        </DialogActions>
      </Dialog>

      {/* Modal d'association aux exercices */}
      <Modal open={assignOpen} onClose={() => setAssignOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 520,
            maxWidth: '95vw',
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Associer la vidéo aux exercices
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {assignVideo?.title}
          </Typography>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={exercises}
            getOptionLabel={(option) => option.title}
            value={exercises.filter((ex) => selectedExIds.includes(ex.id))}
            onChange={(event, newValue) => setSelectedExIds(newValue.map((ex) => ex.id))}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option.id} style={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.title}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Exercices" placeholder="Sélectionner des exercices" />
            )}
            sx={{ mb: 3 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={() => setAssignOpen(false)} color="secondary">Annuler</Button>
            <Button variant="contained" onClick={handleSaveAssign}>Enregistrer</Button>
          </Box>
        </Box>
      </Modal>

      {/* Modal de prévisualisation vidéo */}
      <Modal open={previewOpen} onClose={() => { setPreviewOpen(false); setPreviewVideo(null); }}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: '95vw', md: 720 },
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 2,
          }}
        >
          {previewVideo ? (
            <>
              <Typography variant="h6" sx={{ mb: 2 }}>{previewVideo.title}</Typography>
              {(() => {
                const id = getYouTubeId(previewVideo.url);
                if (id) {
                  return (
                    <Box sx={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden", borderRadius: 2 }}>
                      <iframe
                        src={`https://www.youtube.com/embed/${id}`}
                        title={previewVideo.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", borderRadius: 8 }}
                      />
                    </Box>
                  );
                }
                const lowerUrl = (previewVideo.url || "").toLowerCase();
                const isSelfHosted =
                  lowerUrl.startsWith("/uploads/videos/") ||
                  /\.(mp4|webm|ogg)(?:[?#]|$)/i.test(lowerUrl);

                if (isSelfHosted) {
                  const src = resolveMediaUrl(previewVideo.url);
                  return (
                    <Box sx={{ width: "100%" }}>
                      <Box sx={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden", borderRadius: 2, bgcolor: "black" }}>
                        <video
                          src={src}
                          controls
                          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", borderRadius: 8 }}
                        >
                          Votre navigateur ne supporte pas la lecture vidéo.
                        </video>
                      </Box>
                    </Box>
                  );
                }

                return (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Format de vidéo non reconnu. Ouvrir dans un onglet :
                    </Typography>
                    <Button variant="outlined" onClick={() => window.open(previewVideo.url, '_blank')}>Ouvrir la vidéo</Button>
                  </Box>
                );
              })()}
            </>
          ) : null}
        </Box>
      </Modal>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setErrorMessage(null)} severity="error" sx={{ width: "100%" }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
